import { useState } from "react";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MapPin, Trash2, Plus } from "lucide-react";

export function RoomManager() {
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [calendarId, setCalendarId] = useState("");
  const [location, setLocation] = useState("");
  const [deviceId, setDeviceId] = useState("");

  const { data: rooms, isLoading } = trpc.rooms.list.useQuery();
  const utils = trpc.useUtils();

  const createRoom = trpc.rooms.create.useMutation({
    onSuccess: () => {
      utils.rooms.list.invalidate();
      resetForm();
    },
  });

  const deleteRoom = trpc.rooms.delete.useMutation({
    onSuccess: () => {
      utils.rooms.list.invalidate();
    },
  });

  const resetForm = () => {
    setShowCreate(false);
    setName("");
    setCalendarId("");
    setLocation("");
    setDeviceId("");
  };

  const handleCreate = () => {
    const fullDeviceId = deviceId.length === 8 ? `${deviceId.slice(0, 4)}-${deviceId.slice(4)}` : null;
    createRoom.mutate({
      name,
      google_calendar_id: calendarId,
      location,
      device_id: fullDeviceId,
    });
  };

  const handleDelete = (id: string, roomName: string) => {
    if (window.confirm(`Are you sure you want to delete "${roomName}"?`)) {
      deleteRoom.mutate({ id });
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Rooms</h2>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Add Room
        </Button>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded bg-muted" />
          ))}
        </div>
      ) : !rooms?.length ? (
        <p className="text-sm text-muted-foreground">No rooms configured yet.</p>
      ) : (
        <div className="space-y-2">
          {rooms.map((room) => (
            <div
              key={room.id}
              className="flex items-center justify-between rounded-lg border border-border bg-card p-4"
            >
              <div>
                <p className="font-medium">{room.name}</p>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {room.location}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(room.id, room.name)}
                disabled={deleteRoom.isPending}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Room</DialogTitle>
            <DialogDescription>
              Configure a new meeting room linked to a Google Calendar resource.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="room-name">Room Name</Label>
              <Input
                id="room-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Conference Room A"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="room-calendar">Google Calendar ID</Label>
              <Input
                id="room-calendar"
                value={calendarId}
                onChange={(e) => setCalendarId(e.target.value)}
                placeholder="resource@resource.calendar.google.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="room-location">Location</Label>
              <Input
                id="room-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="2nd Floor, Building A"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="room-device">Device ID <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="mt-1 flex items-center gap-1 font-mono">
                <Input
                  id="room-device-1"
                  value={deviceId.slice(0, 4)}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
                    setDeviceId(val + deviceId.slice(4));
                    if (val.length === 4) document.getElementById("room-device-2")?.focus();
                  }}
                  placeholder="XXXX"
                  maxLength={4}
                  className="w-24 text-center tracking-widest"
                />
                <span className="text-muted-foreground font-bold">-</span>
                <Input
                  id="room-device-2"
                  value={deviceId.slice(4)}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
                    setDeviceId(deviceId.slice(0, 4) + val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && deviceId.slice(4) === "") {
                      document.getElementById("room-device-1")?.focus();
                    }
                  }}
                  placeholder="XXXX"
                  maxLength={4}
                  className="w-24 text-center tracking-widest"
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Shown on the kiosk screen — enter the code displayed on the device</p>
            </div>
            <Button
              onClick={handleCreate}
              disabled={!name || !calendarId || !location || createRoom.isPending}
              className="w-full"
            >
              {createRoom.isPending ? "Creating..." : "Create Room"}
            </Button>
            {createRoom.isError && (
              <p className="text-sm text-destructive">{createRoom.error.message}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
